<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import authService from '../api/auth';
import { getNotificationPermission, requestNotificationPermission, showSystemNotification } from '../utils/notifications';
import { API_BASE_URL } from '../api/baseUrl';
import {
  FileText,
  LayoutDashboard,
  History,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  CheckCircle,
  Zap,
  Eye,
  Copy,
  Search,
  AlertTriangle,
  Trash2,
  ChevronRight,
  ArrowLeft
} from 'lucide-vue-next';

const router = useRouter();

// State
const currentTab = ref<'dashboard' | 'history' | 'billing' | 'settings'>('dashboard');
const user = ref<any>(null);
const isLoading = ref(true);
const isMobileMenuOpen = ref(false);
const showDeleteModal = ref(false);
const deleteEmail = ref('');
const showEditProfileModal = ref(false);
const showChangePasswordModal = ref(false);
const editFirstName = ref('');
const editLastName = ref('');
const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const isUpdatingProfile = ref(false);
const isChangingPassword = ref(false);
const editError = ref('');
const editSuccess = ref('');
const analyses = ref<any[]>([]);
const notifyOnAnalysisComplete = ref(false);
const notificationPermission = ref<NotificationPermission>('default');
const isUpdatingNotificationPreference = ref(false);
const notificationStatusMessage = ref('');

const creditPackages = [
  { amount: 5, price: '$9.99', perUnit: '$1.99/each', popular: false },
  { amount: 20, price: '$29.99', perUnit: '$1.50/each', popular: true },
  { amount: 50, price: '$59.99', perUnit: '$1.20/each', popular: false },
  { amount: 100, price: '$99.99', perUnit: '$0.99/each', popular: false }
];

// Computed
const userInitials = computed(() => {
  if (!user.value) return '';
  return `${user.value.firstName[0]}${user.value.lastName[0]}`.toUpperCase();
});

const userFullName = computed(() => {
  if (!user.value) return '';
  return `${user.value.firstName} ${user.value.lastName}`;
});

// Methods
const loadUser = async () => {
  try {
    const userData = await authService.getCurrentUser();
    user.value = userData;
    notifyOnAnalysisComplete.value = Boolean(userData?.notifyOnAnalysisComplete);
  } catch (error) {
    console.error('Failed to load user:', error);
    router.push('/login');
  } finally {
    isLoading.value = false;
  }
};

const loadAnalysisHistory = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/analysis/history`, {
      headers: {
        'Authorization': `Bearer ${authService.getAuthToken()}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to load analysis history: ${response.status}`);
    }
    
    const data = await response.json();
    analyses.value = data.data || [];
  } catch (error) {
    console.error('Failed to load analysis history:', error);
  }
};

const switchTab = (tab: typeof currentTab.value) => {
  currentTab.value = tab;
  isMobileMenuOpen.value = false;
  
  // Odśwież historię zawsze gdy użytkownik przejdzie na tab "history"
  if (tab === 'history') {
    loadAnalysisHistory();
  }
};

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const logout = async () => {
  authService.logout();
  router.push('/login');
};

const goHome = () => {
  if (currentTab.value === 'dashboard') {
    router.push('/');
  } else {
    currentTab.value = 'dashboard';
  }
};

const goToNewAnalysis = () => {
  router.push('/#demo');
};

const handleBuyCredits = async (amount: number) => {
  try {
    const updatedUser = await authService.addCredits(amount);
    user.value = updatedUser;
  } catch (error) {
    console.error('Failed to add credits:', error);
  }
};

const confirmDelete = async () => {
  if (deleteEmail.value !== user.value.email) {
    alert('Email does not match');
    return;
  }

  try {
    await authService.deleteAccount();
    authService.logout();
    router.push('/login');
  } catch (error) {
    console.error('Failed to delete account:', error);
  }
};

const openEditProfile = () => {
  editError.value = '';
  editSuccess.value = '';
  editFirstName.value = user.value.firstName;
  editLastName.value = user.value.lastName;
  showEditProfileModal.value = true;
};

const updateProfile = async () => {
  if (!editFirstName.value.trim() || !editLastName.value.trim()) {
    editError.value = 'Imię i nazwisko nie mogą być puste';
    return;
  }

  isUpdatingProfile.value = true;
  editError.value = '';
  
  try {
    const updatedUser = await authService.updateProfile(editFirstName.value, editLastName.value);
    user.value = updatedUser;
    editSuccess.value = 'Profil zaktualizowany!';
    setTimeout(() => {
      showEditProfileModal.value = false;
      editSuccess.value = '';
    }, 1500);
  } catch (error) {
    editError.value = error instanceof Error ? error.message : 'Błąd podczas aktualizacji profilu';
    console.error('Failed to update profile:', error);
  } finally {
    isUpdatingProfile.value = false;
  }
};

const openChangePassword = () => {
  editError.value = '';
  editSuccess.value = '';
  currentPassword.value = '';
  newPassword.value = '';
  confirmPassword.value = '';
  showChangePasswordModal.value = true;
};

const changePassword = async () => {
  if (!currentPassword.value || !newPassword.value || !confirmPassword.value) {
    editError.value = 'Wszystkie pola są wymagane';
    return;
  }

  if (newPassword.value !== confirmPassword.value) {
    editError.value = 'Nowe hasła nie są identyczne';
    return;
  }

  if (newPassword.value.length < 6) {
    editError.value = 'Nowe hasło musi mieć co najmniej 6 znaków';
    return;
  }

  isChangingPassword.value = true;
  editError.value = '';
  
  try {
    await authService.changePassword(currentPassword.value, newPassword.value);
    editSuccess.value = 'Hasło zmienione pomyślnie!';
    setTimeout(() => {
      showChangePasswordModal.value = false;
      editSuccess.value = '';
    }, 1500);
  } catch (error) {
    editError.value = error instanceof Error ? error.message : 'Błąd podczas zmiany hasła';
    console.error('Failed to change password:', error);
  } finally {
    isChangingPassword.value = false;
  }
};

const updateNotificationPreference = async (enabled: boolean) => {
  isUpdatingNotificationPreference.value = true;
  notificationStatusMessage.value = '';

  try {
    const updatedUser = await authService.updateNotificationPreference(enabled);
    user.value = updatedUser;
    notifyOnAnalysisComplete.value = Boolean(updatedUser?.notifyOnAnalysisComplete);
    window.dispatchEvent(new CustomEvent('notificationPreferenceChanged', {
      detail: { enabled: notifyOnAnalysisComplete.value }
    }));
    notificationStatusMessage.value = enabled
      ? 'Powiadomienia po analizie są włączone.'
      : 'Powiadomienia po analizie są wyłączone.';
  } catch (error) {
    notifyOnAnalysisComplete.value = !enabled;
    notificationStatusMessage.value = 'Nie udało się zapisać ustawienia. Spróbuj ponownie.';
    console.error('Failed to update notification preference:', error);
  } finally {
    isUpdatingNotificationPreference.value = false;
  }
};

const handleNotificationToggle = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const enabled = target.checked;

  if (enabled && notificationPermission.value !== 'granted') {
    const permission = await requestNotificationPermission();
    notificationPermission.value = permission;

    if (permission !== 'granted') {
      notifyOnAnalysisComplete.value = false;
      target.checked = false;
      await updateNotificationPreference(false);
      notificationStatusMessage.value = 'Aby dostawać powiadomienia, włącz je w ustawieniach przeglądarki.';
      return;
    }
  }

  await updateNotificationPreference(enabled);
};

const sendTestNotification = async () => {
  notificationStatusMessage.value = '';

  if (typeof window === 'undefined' || !('Notification' in window)) {
    notificationStatusMessage.value = 'Ta przeglądarka nie wspiera powiadomień systemowych.';
    return;
  }

  notificationPermission.value = getNotificationPermission();

  if (notificationPermission.value === 'default') {
    const permission = await requestNotificationPermission();
    notificationPermission.value = permission;
  }

  if (notificationPermission.value !== 'granted') {
    notificationStatusMessage.value = 'Powiadomienia są zablokowane. Włącz je w ustawieniach przeglądarki.';
    return;
  }

  try {
    const shown = await showSystemNotification('Test powiadomienia CV-Tailor', {
      body: 'Powiadomienia działają poprawnie ✅',
      tag: 'cv-tailor-test-notification'
    });

    if (!shown) {
      notificationStatusMessage.value = 'Nie udało się pokazać systemowego bannera. Sprawdź w macOS: Ustawienia > Powiadomienia > Twoja przeglądarka (Banery/Alerty) oraz Focus/Do Not Disturb.';
      return;
    }

    notificationStatusMessage.value = 'Wysłano testowe powiadomienie. Jeśli nie widzisz bannera, sprawdź Centrum Powiadomień w macOS.';
  } catch (error) {
    console.error('Failed to show test notification:', error);
    notificationStatusMessage.value = 'Nie udało się wysłać testowego powiadomienia.';
  }
};

onMounted(() => {
  notificationPermission.value = getNotificationPermission();

  if (!authService.isAuthenticated()) {
    router.push('/login');
  } else {
    loadUser();
    loadAnalysisHistory();
  }

  // Słuchaj na event z DemoSimulator - gdy analiza się udała
  window.addEventListener('analysisCompleted', () => {
    console.log('Event analysisCompleted caught, refreshing history...');
    loadAnalysisHistory();
  });
});

// Cleanup listener on unmount
onBeforeUnmount(() => {
  window.removeEventListener('analysisCompleted', loadAnalysisHistory);
});

// Components
const NavItem = {
  props: ['icon', 'label', 'active'],
  emits: ['click'],
  setup(props: any) {
    const iconMap: any = {
      LayoutDashboard,
      History,
      CreditCard,
      Settings
    };

    const Icon = iconMap[props.icon];

    return {
      Icon
    };
  },
  template: `
    <button 
      @click="$emit('click')"
      :class="['w-full flex items-center gap-3 px-4 py-3 rounded-xl transition font-medium', active ? 'bg-slate-900 text-white shadow-lg shadow-indigo-500/20' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900']"
    >
      <component :is="Icon" :class="active ? 'text-indigo-400' : 'text-slate-400'" class="w-5 h-5" />
      {{ label }}
    </button>
  `
};

const HistoryItem = {
  props: ['item'],
  data() {
    return {
      isTextClamped: false,
      showTooltip: false,
      tooltipTop: 0,
      tooltipLeft: 0,
      tooltipMaxHeight: 220,
      tooltipPlacement: 'bottom',
      tooltipHideTimeout: null,
    };
  },
  setup() {
    const copyToClipboard = async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
      } catch (err) {
        console.error('Copy error:', err);
      }
    };

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('pl-PL', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    };

    return {
      FileText,
      Eye,
      Copy,
      ChevronRight,
      copyToClipboard,
      formatDate
    };
  },
  mounted() {
    // Czekaj na render i sprawdź czy tekst jest obcięty
    this.$nextTick(() => {
      setTimeout(() => {
        this.checkIfClamped();
      }, 50);
    });
    // Bind aby zachować context
    this.handleResize = () => this.checkIfClamped();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    if (this.tooltipHideTimeout) {
      window.clearTimeout(this.tooltipHideTimeout);
      this.tooltipHideTimeout = null;
    }
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    clearTooltipHideTimeout() {
      if (this.tooltipHideTimeout) {
        window.clearTimeout(this.tooltipHideTimeout);
        this.tooltipHideTimeout = null;
      }
    },
    checkIfClamped() {
      const titleElement = this.$el.querySelector('h4');
      if (titleElement) {
        // Metoda 1: Sprawdzenie scrollHeight vs clientHeight
        const scrollHeight = titleElement.scrollHeight;
        const clientHeight = titleElement.clientHeight;
        const methodOne = scrollHeight > clientHeight;
        
        // Metoda 2: Sprawdzenie czy jest więcej tekstu niż się mieści w 2 liniach
        // Klonujemy element bez line-clamp
        const clone = titleElement.cloneNode(true);
        clone.style.display = 'none';
        clone.style.maxWidth = titleElement.clientWidth + 'px';
        clone.classList.remove('line-clamp-2');
        clone.style.all = 'inherit';
        titleElement.parentElement?.appendChild(clone);
        
        const cloneHeight = clone.scrollHeight;
        const methodTwo = cloneHeight > titleElement.clientHeight;
        if (clone.parentElement) {
          clone.parentElement.removeChild(clone);
        }
        
        const isClamped = methodOne || methodTwo;
        this.isTextClamped = isClamped;
        
        console.log('Clamp check:', {
          text: titleElement.textContent.substring(0, 50),
          methodOne: { scrollHeight, clientHeight, result: methodOne },
          methodTwo: { cloneHeight, clientHeight, result: methodTwo },
          final: isClamped
        });
      }
    },
    handleTooltipEnter(event: MouseEvent) {
      if (!this.isTextClamped) {
        return;
      }

      this.clearTooltipHideTimeout();

      const titleElement = event.target as HTMLElement;
      const rect = titleElement.getBoundingClientRect();

      const tooltipWidth = 384;
      const viewportPadding = 16;
      const verticalGap = 10;
      const minPreferredSpace = 140;
      const halfTooltip = tooltipWidth / 2;

      let left = rect.left + rect.width / 2;
      left = Math.max(halfTooltip + viewportPadding, left);
      left = Math.min(window.innerWidth - halfTooltip - viewportPadding, left);

      const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
      const spaceAbove = rect.top - viewportPadding;
      const shouldShowAbove = spaceBelow < minPreferredSpace && spaceAbove > spaceBelow;

      this.tooltipPlacement = shouldShowAbove ? 'top' : 'bottom';

      const availableSpace = shouldShowAbove ? spaceAbove : spaceBelow;
      this.tooltipMaxHeight = Math.max(40, Math.min(320, availableSpace - verticalGap));

      if (shouldShowAbove) {
        this.tooltipTop = Math.max(rect.top - verticalGap, viewportPadding + 40);
      } else {
        this.tooltipTop = Math.min(rect.bottom + verticalGap, window.innerHeight - viewportPadding - 40);
      }

      this.tooltipLeft = left;
      this.showTooltip = true;
    },
    handleTooltipLeave() {
      this.clearTooltipHideTimeout();
      this.tooltipHideTimeout = window.setTimeout(() => {
        this.showTooltip = false;
        this.tooltipHideTimeout = null;
      }, 120);
    },
    handleTooltipBoxEnter() {
      this.clearTooltipHideTimeout();
      this.showTooltip = true;
    }
  },
  template: `
    <div class="p-5 border-b border-slate-100 hover:bg-slate-50/80 transition group last:border-0">
      <div class="flex flex-col md:flex-row justify-between gap-4 md:items-center">
        <div class="flex items-start gap-4">
          <div class="bg-indigo-50 p-3 rounded-lg hidden sm:block">
            <component :is="FileText" class="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <div class="relative">
              <h4 
                class="font-bold text-slate-900 line-clamp-2" 
                :class="{ 'cursor-help': isTextClamped }"
                @mouseenter="handleTooltipEnter"
                @mouseleave="handleTooltipLeave"
              >
                {{ item.jobDescription || item.jobTitle }}
              </h4>
              <teleport to="body">
                <div
                  v-if="isTextClamped && showTooltip"
                  @mouseenter="handleTooltipBoxEnter"
                  @mouseleave="handleTooltipLeave"
                  :style="{ top: tooltipTop + 'px', left: tooltipLeft + 'px', zIndex: 9999, transform: tooltipPlacement === 'top' ? 'translate(-50%, -100%)' : 'translateX(-50%)', maxHeight: tooltipMaxHeight + 'px' }"
                  class="fixed pointer-events-auto bg-white border border-slate-200 text-slate-700 text-sm rounded-xl px-4 py-2.5 whitespace-normal max-w-sm overflow-y-auto shadow-xl"
                >
                  {{ item.jobDescription || item.jobTitle }}
                  <div v-if="tooltipPlacement === 'bottom'" class="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white border-l border-t border-slate-200 rotate-45"></div>
                  <div v-else class="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white border-r border-b border-slate-200 rotate-45"></div>
                </div>
              </teleport>
            </div>
            <p class="text-xs text-slate-500 flex items-center gap-2 mt-1">
              <span>{{ item.cvFileName }}</span>
              <span class="w-1 h-1 bg-slate-300 rounded-full"></span>
              <span>{{ formatDate(item.createdAt) }}</span>
            </p>
          </div>
        </div>
        <div class="flex items-center justify-between md:justify-end gap-6 w-full md:w-auto">
          <div class="flex items-center gap-3">
            <div class="text-center">
              <span class=\"block text-xs text-slate-400 font-medium\">Przed</span>
              <span class=\"font-bold text-slate-400\">{{ item.scoreBefore }}%</span>
            </div>
            <component :is="ChevronRight" class="w-4 h-4 text-slate-300" />
            <div class=\"text-center\">
              <span class=\"block text-xs text-emerald-600 font-bold\">Po</span>
              <span class="font-bold text-emerald-600 text-lg">{{ item.scoreAfter }}%</span>
            </div>
          </div>
          <div class="flex gap-2">
            <button @click="copyToClipboard(item.optimizedCV || '')" class="p-2 text-slate-400 hover:text-emerald-600 hover:bg-white rounded-lg transition" title="Skopiuj zoptymalizowane CV">
              <component :is="Copy" class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  `
};
</script>

<template>
  <div v-if="isLoading" class="flex items-center justify-center min-h-screen">
    <div class="text-center">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
      <p class="text-slate-600">Wczytywanie...</p>
    </div>
  </div>

  <div v-else class="flex h-screen overflow-hidden bg-slate-50">
    <!-- Mobile Menu Toggle -->
    <div class="md:hidden fixed top-4 left-4 z-50">
      <button
        @click="toggleMobileMenu"
        class="p-2 bg-white rounded-lg shadow-md border border-slate-200"
      >
        <Menu v-if="!isMobileMenuOpen" class="w-6 h-6" />
        <X v-else class="w-6 h-6" />
      </button>
    </div>

    <!-- Sidebar -->
    <aside
      :class="[
        'fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 md:relative md:translate-x-0',
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      ]"
    >
      <div class="flex flex-col h-full">
        <!-- Header -->
        <div class="h-20 flex items-center px-8 border-b border-slate-100">
          <FileText class="h-8 w-8 text-indigo-600 mr-2" />
          <span class="text-xl font-bold text-slate-900">CV-Tailor</span>
        </div>

        <!-- Navigation -->
        <div class="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          <nav-item
            icon="LayoutDashboard"
            label="Pulpit"
            :active="currentTab === 'dashboard'"
            @click="switchTab('dashboard')"
          />
          <nav-item
            icon="History"
            label="Historia Analiz"
            :active="currentTab === 'history'"
            @click="switchTab('history')"
          />
          <nav-item
            icon="CreditCard"
            label="Płatności i Kredyty"
            :active="currentTab === 'billing'"
            @click="switchTab('billing')"
          />
          <nav-item
            icon="Settings"
            label="Ustawienia Konta"
            :active="currentTab === 'settings'"
            @click="switchTab('settings')"
          />
        </div>

        <!-- Credits Card -->
        <div class="p-4 border-t border-slate-100">
          <div class="bg-indigo-50 rounded-xl p-4 mb-4">
            <div class="flex justify-between items-center mb-2">
              <span class="text-xs font-bold text-indigo-600 uppercase tracking-wide">Dostępne Kredyty</span>
              <Zap class="h-4 w-4 text-indigo-500" />
            </div>
            <div class="text-2xl font-extrabold text-indigo-900">{{ user?.credits || 0 }}</div>
            <button
              @click="switchTab('billing')"
              class="mt-3 w-full text-xs font-semibold text-white bg-indigo-600 py-2 rounded-lg hover:bg-indigo-700 transition"
            >
              Kup Więcej
            </button>
          </div>
          <button
            @click="logout"
            class="flex items-center gap-3 w-full px-4 py-3 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition"
          >
            <LogOut class="w-5 h-5" />
            <span class="font-medium">Wyloguj się</span>
          </button>
        </div>
      </div>
    </aside>

    <!-- Main Content -->
    <main class="flex-1 overflow-y-auto p-4 md:p-8 pt-16 md:pt-8">
      <!-- Back Button -->
      <div class="mb-6">
        <button
          @click="goHome"
          class="flex items-center gap-2 text-slate-600 hover:text-indigo-600 font-medium transition"
        >
          <ArrowLeft class="h-5 w-5" />
          {{ currentTab === 'dashboard' ? 'Powrót do strony głównej' : 'Powrót' }}
        </button>
      </div>

      <!-- DASHBOARD TAB -->
      <div v-if="currentTab === 'dashboard'" class="max-w-5xl mx-auto space-y-8">
        <header class="flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-bold text-slate-900">Cześć, {{ user?.firstName }}! 👋</h1>
            <p class="text-slate-500 mt-1">Tutaj znajdziesz podsumowanie aktywności optymalizacji CV.</p>
          </div>
          <button @click="goToNewAnalysis" class="hidden md:flex items-center gap-2 bg-slate-900 text-white px-5 py-3 rounded-xl font-medium hover:bg-slate-800 transition shadow-lg shadow-indigo-500/20">
            <Plus class="w-5 h-5" />
            Nowa Analiza
          </button>
        </header>

        <div class="grid md:grid-cols-3 gap-6">
          <div class="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <div class="flex items-center gap-4">
              <div class="p-3 bg-indigo-100 text-indigo-600 rounded-xl">
                <History class="w-6 h-6" />
              </div>
              <div>
                <p class="text-sm text-slate-500 font-medium">Wszystkich Analiz</p>
                <p class="text-2xl font-bold text-slate-900">{{ analyses.length }}</p>
              </div>
            </div>
          </div>

          <!--
          <div class="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <div class="flex items-center gap-4">
              <div class="p-3 bg-emerald-100 text-emerald-600 rounded-xl">
                <CheckCircle class="w-6 h-6" />
              </div>
              <div>
                <p class="text-sm text-slate-500 font-medium">Średnia Ocena</p>
                <p class="text-2xl font-bold text-slate-900">88%</p>
              </div>
            </div>
          </div>
          -->

          <div class="md:col-span-2 bg-gradient-to-br from-indigo-600 to-violet-700 p-6 rounded-2xl text-white shadow-lg shadow-indigo-500/30">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-indigo-100 font-medium mb-1">Twoje Kredyty</p>
                <p class="text-3xl font-bold">{{ user?.credits || 0 }}</p>
              </div>
              <button
                @click="switchTab('billing')"
                class="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition backdrop-blur-sm"
              >
                <Plus class="w-5 h-5" />
              </button>
            </div>
            <div class="mt-4 h-1.5 bg-indigo-900/30 rounded-full overflow-hidden">
              <div class="h-full bg-white/80 w-[20%]"></div>
            </div>
            <p class="text-xs text-indigo-200 mt-2">12 z 50 użytych tego miesiąca</p>
          </div>
        </div>

        <section>
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-xl font-bold text-slate-900">Ostatnie Optymalizacje</h3>
            <button @click="switchTab('history')" class="text-sm text-indigo-600 font-semibold hover:text-indigo-700">
              Pokaż wszystkie
            </button>
          </div>
          <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <history-item v-for="item in analyses.slice(0, 3)" :key="item.id" :item="item" />
          </div>
        </section>
      </div>

      <!-- HISTORY TAB -->
      <div v-if="currentTab === 'history'" class="max-w-5xl mx-auto">
        <header class="mb-8">
          <h1 class="text-3xl font-bold text-slate-900">Historia Analiz</h1>
          <p class="text-slate-500 mt-1">Przeglądaj, kopiuj i zarządzaj wersjami CV.</p>
        </header>

        <div class="relative mb-6">
          <input
            type="text"
            placeholder="Szukaj po stanowisku lub nazwie pliku..."
            class="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
          />
          <Search class="absolute left-3 top-3.5 text-slate-400 h-5 w-5" />
        </div>

        <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div v-if="analyses.length === 0" class="p-8 text-center">
            <p class="text-slate-500">Nie masz jeszcze żadnych analiz. Wróć do strony głównej i przeanalizuj swoje CV.</p>
          </div>
          <history-item v-for="item in analyses" :key="item.id" :item="item" />
        </div>
      </div>

      <!-- BILLING TAB -->
      <div v-if="currentTab === 'billing'" class="max-w-5xl mx-auto space-y-10">
        <header class="text-center max-w-2xl mx-auto">
          <h1 class="text-3xl font-bold text-slate-900">Kup Kredyty</h1>
          <p class="text-slate-500 mt-2">Wybierz pakiet dostosowany do Twoich potrzeb. Kredyty nigdy nie wygasają.</p>
        </header>

        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div
            v-for="(pkg, idx) in creditPackages"
            :key="idx"
            :class="[
              'relative bg-white rounded-2xl border p-6 flex flex-col items-center text-center transition hover:scale-105',
              pkg.popular
                ? 'border-indigo-500 shadow-xl shadow-indigo-500/10 ring-1 ring-indigo-500'
                : 'border-slate-200 hover:border-indigo-300 hover:shadow-lg'
            ]"
          >
            <span
              v-if="pkg.popular"
              class="absolute -top-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-xs font-bold px-3 py-1 rounded-full"
            >
              Najpopularniejszy
            </span>
            <div class="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-4 text-indigo-600 font-bold text-xl">
              {{ pkg.amount }}
            </div>
            <h3 class="font-bold text-slate-900 text-lg mb-1">{{ pkg.amount }} Kredytów</h3>
            <div class="my-4">
              <span class="text-3xl font-extrabold text-slate-900">{{ pkg.price }}</span>
            </div>
            <p class="text-xs text-slate-500 mb-6">{{ pkg.perUnit }}</p>
            <button
              @click="handleBuyCredits(pkg.amount)"
              :class="[
                'w-full py-2.5 rounded-xl font-bold text-sm transition',
                pkg.popular ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              ]"
            >
              Wybieram
            </button>
          </div>
        </div>

        <div class="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
          <div class="flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <h3 class="text-lg font-bold text-slate-900 flex items-center gap-2">
                <div class="w-2 h-2 rounded-full bg-emerald-500"></div>
                Aktywny Abonament: Plan Pro
              </h3>
              <p class="text-slate-500 text-sm mt-1">Następne odnowienie: 14 listopada 2026</p>
            </div>
            <div class="flex gap-4">
              <button class="text-slate-500 hover:text-slate-800 text-sm font-medium underline decoration-slate-300 hover:decoration-slate-800 transition">
                Historia Faktur
              </button>
              <button class="text-red-500 hover:text-red-700 text-sm font-medium border border-red-100 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg transition">
                Anuluj Abonament
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- SETTINGS TAB -->
      <div v-if="currentTab === 'settings'" class="max-w-3xl mx-auto space-y-8">
        <header>
          <h1 class="text-3xl font-bold text-slate-900">Ustawienia</h1>
        </header>

        <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div class="p-6 border-b border-slate-100">
            <h3 class="text-lg font-bold text-slate-900 mb-4">Profil</h3>
            <div class="flex items-center gap-4">
              <div class="h-16 w-16 rounded-full bg-gradient-to-tr from-indigo-500 to-violet-500 flex items-center justify-center text-white text-xl font-bold">
                {{ userInitials }}
              </div>
              <div>
                <p class="font-bold text-slate-900">{{ userFullName }}</p>
                <p class="text-slate-500 text-sm">{{ user?.email }}</p>
              </div>
              <button @click="openEditProfile" class="ml-auto text-sm text-indigo-600 font-medium hover:text-indigo-700">Edytuj</button>
            </div>
          </div>

          <div class="p-6 border-t border-slate-100">
            <h3 class="text-lg font-bold text-slate-900 mb-4">Bezpieczeństwo</h3>
            <button @click="openChangePassword" class="text-sm text-indigo-600 font-medium hover:text-indigo-700">
              Zmień Hasło
            </button>
          </div>

          <div class="p-6 border-t border-slate-100">
            <h3 class="text-lg font-bold text-slate-900 mb-4">Powiadomienia</h3>
            <div class="space-y-4">
              <label class="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  :checked="notifyOnAnalysisComplete"
                  :disabled="isUpdatingNotificationPreference"
                  class="w-5 h-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-60"
                  @change="handleNotificationToggle"
                />
                <span class="text-slate-700">Powiadom mnie po zakończeniu analizy</span>
              </label>
              <p class="text-xs text-slate-500">
                Status przeglądarki:
                <span class="font-semibold text-slate-700">{{ notificationPermission }}</span>
              </p>
              <button
                type="button"
                class="text-xs font-medium text-indigo-600 hover:text-indigo-700 disabled:text-slate-400 disabled:cursor-not-allowed"
                @click="sendTestNotification"
              >
                Wyślij testowe powiadomienie
              </button>
              <p v-if="isUpdatingNotificationPreference" class="text-xs text-slate-500">Zapisywanie ustawienia...</p>
              <p v-if="notificationStatusMessage" class="text-xs text-amber-600">{{ notificationStatusMessage }}</p>
            </div>
          </div>
        </div>

        <div class="border border-red-200 rounded-2xl overflow-hidden bg-red-50/30">
          <div class="p-6">
            <h3 class="text-lg font-bold text-red-700 flex items-center gap-2">
              <AlertTriangle class="h-5 w-5" />
              Uwaga
            </h3>
            <p class="text-red-600/80 text-sm mt-2 mb-6">Usunięcie konta jest nieodwracalne. Utracisz dostęp do wszystkich wygenerowanych CV, historii oraz niewykorzystanych kredytów.</p>
            <button
              @click="showDeleteModal = true"
              class="bg-white border border-red-200 text-red-600 px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-red-50 transition"
            >
              Trwale Usuń Konto
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- Edit Profile Modal -->
    <div v-if="showEditProfileModal" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 class="text-xl font-bold text-slate-900 mb-6">Edytuj Profil</h3>
        
        <div v-if="editError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {{ editError }}
        </div>
        
        <div v-if="editSuccess" class="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm">
          {{ editSuccess }}
        </div>

        <div class="space-y-4 mb-6">
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">Imię</label>
            <input
              v-model="editFirstName"
              type="text"
              placeholder="Twoje imię"
              class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">Nazwisko</label>
            <input
              v-model="editLastName"
              type="text"
              placeholder="Twoje nazwisko"
              class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div class="flex gap-3">
          <button
            @click="showEditProfileModal = false"
            class="flex-1 py-3 border border-slate-300 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition"
          >
            Anuluj
          </button>
          <button
            @click="updateProfile"
            :disabled="isUpdatingProfile"
            class="flex-1 py-3 bg-indigo-600 rounded-xl font-semibold text-white hover:bg-indigo-700 transition shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isUpdatingProfile ? 'Zapisywanie...' : 'Zapisz' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Change Password Modal -->
    <div v-if="showChangePasswordModal" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 class="text-xl font-bold text-slate-900 mb-6">Zmień Hasło</h3>
        
        <div v-if="editError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {{ editError }}
        </div>
        
        <div v-if="editSuccess" class="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm">
          {{ editSuccess }}
        </div>

        <div class="space-y-4 mb-6">
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">Obecne Hasło</label>
            <input
              v-model="currentPassword"
              type="password"
              placeholder="Wpisz obecne hasło"
              class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">Nowe Hasło</label>
            <input
              v-model="newPassword"
              type="password"
              placeholder="Wpisz nowe hasło"
              class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">Potwierdź Hasło</label>
            <input
              v-model="confirmPassword"
              type="password"
              placeholder="Potwierdź nowe hasło"
              class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div class="flex gap-3">
          <button
            @click="showChangePasswordModal = false"
            class="flex-1 py-3 border border-slate-300 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition"
          >
            Anuluj
          </button>
          <button
            @click="changePassword"
            :disabled="isChangingPassword"
            class="flex-1 py-3 bg-indigo-600 rounded-xl font-semibold text-white hover:bg-indigo-700 transition shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isChangingPassword ? 'Zmiana...' : 'Zmień Hasło' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Account Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div class="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4 text-red-600 mx-auto">
          <Trash2 class="w-6 h-6" />
        </div>
        <h3 class="text-xl font-bold text-center text-slate-900 mb-2">Jesteś pewny?</h3>
        <p class="text-center text-slate-500 mb-4 text-sm">To działanie nie może być cofnięte. Wpisz swój email, aby potwierdzić:</p>
        <input
          v-model="deleteEmail"
          type="email"
          :placeholder="user?.email"
          class="w-full px-4 py-3 border border-slate-200 rounded-xl mb-6 focus:outline-none focus:ring-2 focus:ring-red-500"
        />
        <div class="flex gap-3">
          <button
            @click="showDeleteModal = false"
            class="flex-1 py-3 border border-slate-300 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition"
          >
            Anuluj
          </button>
          <button
            @click="confirmDelete"
            class="flex-1 py-3 bg-red-600 rounded-xl font-semibold text-white hover:bg-red-700 transition shadow-lg shadow-red-500/20"
          >
            Usuń Konto
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
